import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2 } from "lucide-react";

export default function SetPasswordPage() {
  const { settings } = useSettings();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("La password deve avere almeno 6 caratteri."); return; }
    if (password !== confirm) { setError("Le password non coincidono."); return; }
    setLoading(true);
    try {
      await api.post("/auth/set-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <BrandLogo logoUrl={settings.logo_url} size={44} />
          <div>
            <p className="font-display font-extrabold">Gestione Servizi CRI</p>
            <p className="text-sm text-muted-foreground">{settings.committee_name}</p>
          </div>
        </div>

        {done ? (
          <div className="rounded-xl border border-border bg-card p-8 text-center">
            <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
            <h2 className="font-display text-2xl font-bold mt-3">Password impostata</h2>
            <p className="text-muted-foreground mt-1">Ora puoi accedere con la tua email e la nuova password.</p>
            <Button data-testid="go-login" onClick={() => navigate("/auth")} className="rounded-full font-semibold mt-6 w-full">Vai all'accesso</Button>
          </div>
        ) : !token ? (
          <div className="rounded-xl border border-border bg-card p-8 text-center">
            <h2 className="font-display text-2xl font-bold">Link non valido</h2>
            <p className="text-muted-foreground mt-1">Manca il codice di attivazione. Controlla il link ricevuto via email.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="rounded-xl border border-border bg-card p-8 space-y-4" data-testid="set-password-form">
            <h2 className="font-display text-2xl font-bold">Imposta la tua password</h2>
            <div className="space-y-2">
              <Label htmlFor="pw">Nuova password</Label>
              <Input id="pw" type="password" data-testid="new-password-input" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cf">Conferma password</Label>
              <Input id="cf" type="password" data-testid="confirm-password-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
            </div>
            {error && <p data-testid="set-password-error" className="text-sm text-destructive font-medium bg-destructive/10 rounded-lg px-3 py-2">{error}</p>}
            <Button type="submit" data-testid="set-password-submit" disabled={loading} className="rounded-full font-semibold w-full h-11">
              {loading ? "Attendere…" : "Imposta password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
